import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Typography from "@material-ui/core/Typography";
import {
  IconButton,
  Table,
  TextField,
  Box,
  Theme,
  createStyles,
} from "@material-ui/core";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import { Delete } from "@material-ui/icons";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Button from "@material-ui/core/Button";
import { fetchShops } from "redux/actions/shop";
import CreateCourseReq from "types/req/course/CreateCourseReq";
import { addCourse } from "redux/actions/course";
import CourseRow from "./components/CourseRow";
import CreateCourseBaseData from "./components/CreateCourseBaseData";
import { makeStyles } from "@material-ui/core/styles";
import { fetchNomination } from "redux/actions/nomination";
import { fetchNotelClass } from "redux/actions/notelClass";
import CsvImport from "components/CsvImport";
import { CsvFieldType } from "utils/CsvUtils";
export type CourseProp = {
  name: string;
  price: number;
  classId: number;
  nominationId: number;
  shop: number;
  hotel: { enable: boolean; shop: number; cast: number; total: number };
  time: { time: number; shop: number; cast: number };
  welfare: number;
  course: { shop: number; cast: number };
  sort: number | null;
};

const Course: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();
  const loginStaff = useSelector((state) => state.account.staff);
  const shops = useSelector((state) => state.shop);
  const nominations = useSelector((state) => state.nomination);
  const notelClasses = useSelector((state) => state.notelClass);
  const [addCourseBase, setCourse] = React.useState<CourseProp[]>([]);
  const [openCsvImport, setOpenCsvImport] = useState(false);

  useEffect(() => {
    dispatch(fetchShops(loginStaff.companyId));
    dispatch(fetchNotelClass(loginStaff.companyId));
    dispatch(fetchNomination(loginStaff.companyId));
  }, [dispatch, loginStaff]);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: { margin: theme.spacing(1) },
    })
  );

  const classes = useStyles();

  const courseRows = [
    t("course.headers.shopName"),
    t("course.headers.courseName"),
    t("course.headers.hotelIncluded"),
    t("course.headers.className"),
    t("course.headers.time"),
    t("course.headers.nominationKind"),
    t("course.headers.welfare"),
    t("course.headers.coursePrice"),
    t("course.headers.sort"),
    "",
  ];

  const removeCourseTableRow = (index: number) => () => {
    setCourse(addCourseBase.filter((_, i) => index !== i));
  };

  const setCourseName =
    (index: number) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const copyTableRows = addCourseBase.slice();
      copyTableRows[index].name = event.target.value;
      setCourse(copyTableRows);
    };

  const setWelfareFee =
    (index: number) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const copyTableRows = addCourseBase.slice();
      copyTableRows[index].welfare = Number(event.target.value);
      setCourse(copyTableRows);
    };

  const setCourseSort =
    (index: number) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const copyTableRows = addCourseBase.slice();
      copyTableRows[index].sort =
        event.target.value === "" || null || undefined
          ? null
          : Number(event.target.value);
      setCourse(copyTableRows);
    };

  const registerCourseData = async () => {
    const createCourseReq = addCourseBase.map(
      (req) =>
        new CreateCourseReq({
          name: req.name,
          time: req.time.time,
          timeFee: req.time.cast + req.time.shop,
          timeShopFee: req.time.shop,
          timeCastFee: req.time.cast,
          welfareFee: req.welfare,
          hotelFee: req.hotel.total,
          hotelCastFee: req.hotel.cast,
          hotelShopFee: req.hotel.shop,
          totalFee: req.price,
          shopId: req.shop,
          notelClassId: req.classId,
          nominationId: req.nominationId,
          sort: req.sort,
        })
    );
    dispatch(addCourse(loginStaff.companyId, createCourseReq));
    setCourse([]);
  };

  const csvHeader = [
    {
      label: t("course.headers.courseName"),
      key: "name",
      type: CsvFieldType.Text,
    },
    {
      label: t("course.fields.shop"),
      key: "shopId",
      type: CsvFieldType.Object,
      relation: shops,
      relationKey: "name",
      relationId: "shopId",
    },
    {
      label: t("course.headers.className"),
      key: "notelClassId",
      type: CsvFieldType.Object,
      relation: notelClasses,
      relationKey: "name",
      relationId: "notelClassId",
    },
    {
      label: t("course.headers.nominationKind"),
      key: "nominationId",
      type: CsvFieldType.Object,
      relation: nominations,
      relationKey: "name",
      relationId: "nominationId",
    },
    {
      label: t("course.csvFields.time"),
      key: "time",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.basePrice"),
      key: "timeFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.shopBasePrice"),
      key: "timeShopFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.castBasePrice"),
      key: "timeCastFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.welfare"),
      key: "welfareFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.hotel"),
      key: "hotelFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.castHotel"),
      key: "hotelCastFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.shopHotel"),
      key: "hotelShopFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.csvFields.total"),
      key: "totalFee",
      type: CsvFieldType.Number,
    },
    {
      label: t("course.headers.sort"),
      key: "sort",
      type: CsvFieldType.Number,
    },
  ];

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        marginTop={1}
        marginRight={2}
        style={{ backgroundColor: "#fff" }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenCsvImport((prev) => !prev)}
          style={{ marginLeft: "30px" }}
        >
          {t("course.csvImport")}
        </Button>
      </Box>
      {openCsvImport && (
        <CsvImport
          templatePath={"/csv/course.csv"}
          templateName={"course_template.csv"}
          csvHeader={csvHeader}
          addType={CreateCourseReq}
          addFunc={(formData) => addCourse(loginStaff.companyId, formData)}
        />
      )}
      <CreateCourseBaseData setCourse={setCourse} />
      <Card>
        <CardContent>
          <CourseRow />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle1">{t("course.newCourseListTitle")}</Typography>
          <Table>
            <TableHead>
              <TableRow>
                {courseRows.map((row, index) => (
                  <TableCell key={index}>{row}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {addCourseBase.map((c, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {shops.find((shop) => shop.shopId === c.shop)?.name}
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      onChange={setCourseName(index)}
                    />
                  </TableCell>
                  <TableCell>{c.hotel.enable ? t("course.hotelIncluded") : t("course.hotelNotIncluded")}</TableCell>
                  <TableCell>
                    {notelClasses.find(
                      (notelClass) => notelClass.notelClassId === c.classId
                    )?.name || t("course.unset")}
                  </TableCell>
                  <TableCell>{c.time.time}</TableCell>
                  <TableCell>
                    {nominations.find(
                      (nomination) => nomination.nominationId === c.nominationId
                    )?.name || t("course.unset")}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <TextField
                        label={t("course.fields.shop")}
                        variant="outlined"
                        onChange={setWelfareFee(index)}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <TextField
                        label={t("course.fields.basePrice")}
                        variant="outlined"
                        defaultValue={c.time.cast + c.time.shop}
                        disabled
                      />
                      <TextField
                        label={t("course.fields.hotelIncluded")}
                        variant="outlined"
                        defaultValue={c.hotel.total}
                        disabled
                      />
                      <TextField
                        label={t("course.fields.total")}
                        variant="outlined"
                        disabled
                        defaultValue={c.hotel.total + c.time.cast + c.time.shop}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <TextField
                        label={t("course.fields.sort")}
                        variant="outlined"
                        defaultValue={c.sort}
                        onChange={setCourseSort(index)}
                      />
                    </Box>
                  </TableCell>
                  <TableCell padding="checkbox">
                    <IconButton onClick={removeCourseTableRow(index)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" justifyContent="center" style={{ margin: 10 }}>
            <Button
              variant="contained"
              className={classes.button}
              color="primary"
              disabled={addCourseBase.length === 0}
              onClick={registerCourseData}
            >
              {t("course.registerButton")}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default Course;
